import * as React from 'react'
import { Check } from 'lucide-react'
import { useI18n } from '@/app/lib/i18n'
import { useCertificationContentDefinitions } from '@/app/lib/i18n/useCertificationContentDefinitions'
import {
  getYoutubeThumbnailUrl,
  getYoutubeVideoId,
  isYoutubeUrl,
} from '@/app/lib/media/youtube'
import type { CertificationTrainingUserProgress } from '@/lib/api/certifications'
import { ImageLoader } from '@/ui/components/ImageLoader'
import { YoutubeIcon } from '@/ui/components/Icon'
import * as styles from './CoursePage.css'

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  )
}

const LESSON_IMGS = [
  '/images/certification/lesson-1.png',
  '/images/certification/lesson-2.png',
  '/images/certification/lesson-3.png',
  '/images/certification/lesson-4.png',
  '/images/certification/lesson-5.png',
  '/images/certification/lesson-6.png',
]

export function lessonCardFallbackImg(index: number) {
  return LESSON_IMGS[index % LESSON_IMGS.length]
}

function formatAttemptedAt(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export type CourseLessonCardProps = {
  title: string
  section: string
  videoUrl?: string | null
  thumbnailUrl?: string | null
  stepNumber: number
  /** Estimated section duration in minutes (video + quiz). */
  estimatedMinutes?: number
  questionCount?: number
  userProgress?: CertificationTrainingUserProgress | null
  /** High-water play percent for this lesson video (0–100). */
  videoWatchedPercent?: number
  /** Fallback art index when no thumbnail / YouTube poster is available. */
  fallbackIndex?: number
  interactive?: boolean
  /**
   * When true, omit Watch/Quiz/Start actions — used for hover previews that
   * should only show lesson info (image, title, duration, etc.).
   */
  previewOnly?: boolean
  onWatchVideo?: () => void
  onTakeQuiz?: () => void
  onStartLesson?: () => void
}

export function CourseLessonCard({
  title,
  section,
  videoUrl,
  thumbnailUrl,
  stepNumber,
  estimatedMinutes = 8,
  questionCount,
  userProgress,
  videoWatchedPercent = 0,
  fallbackIndex = 0,
  interactive = true,
  previewOnly = false,
  onWatchVideo,
  onTakeQuiz,
  onStartLesson,
}: CourseLessonCardProps) {
  const { t, tx } = useI18n()
  const { resolve } = useCertificationContentDefinitions()
  const displayTitle = resolve(title).trim() || title
  const displaySection = resolve(section).trim() || section
  const ytId = videoUrl ? getYoutubeVideoId(videoUrl) : null
  const thumb =
    thumbnailUrl
    || (ytId ? getYoutubeThumbnailUrl(ytId) : null)
    || lessonCardFallbackImg(fallbackIndex)

  const passed = Boolean(userProgress?.passed)
  const questionsLabel =
    questionCount != null && questionCount > 0
      ? t('certification.course.questionsCount', { count: questionCount })
      : null

  return (
    <div className={[styles.lessonCard, passed ? styles.lessonCardComplete : ''].filter(Boolean).join(' ')}>
      <div className={styles.cardImageWrap}>
        <ImageLoader
          src={thumb}
          alt={displayTitle}
          className={styles.cardImage}
        />
        {passed && <div className={styles.cardImageCompleteOverlay} aria-hidden />}
        {isYoutubeUrl(videoUrl) && (
          <span
            className={styles.cardYoutubeBadge}
            title={t('certification.course.youtubeVideo')}
            aria-label={t('certification.course.youtubeVideo')}
          >
            <YoutubeIcon size={14} />
          </span>
        )}
        <span className={styles.cardTimeBadge}>
          {t('certification.course.minutesBadge', { count: estimatedMinutes })}
        </span>
        {passed && (
          <span className={styles.cardCompleteCheck} aria-label={t('certification.course.completed')}>
            <Check size={16} strokeWidth={2.5} aria-hidden />
          </span>
        )}
      </div>

      <div className={styles.cardBody}>
        <p className={styles.cardTitle}>
          {stepNumber}. {displayTitle}
        </p>
        <p className={styles.cardDesc}>{displaySection}</p>
        {questionsLabel && (
          <p className={styles.cardMeta}>{questionsLabel}</p>
        )}
        {previewOnly && (
          <div className={styles.cardPreviewProgress}>
            {userProgress ? (
              <>
                <p className={styles.cardMeta}>
                  {t('certification.course.scoreSummary', {
                    correct: userProgress.correctCount,
                    total: userProgress.totalCount,
                    score: userProgress.score,
                  })}
                </p>
                {userProgress.attemptCount != null && userProgress.attemptCount > 0 && (
                  <p className={styles.cardMeta}>
                    {t('certification.course.attemptCount', {
                      count: userProgress.attemptCount,
                    })}
                  </p>
                )}
                {userProgress.attemptedAt && (
                  <p className={styles.cardMeta}>
                    {t('certification.course.lastAttempt', {
                      when: formatAttemptedAt(userProgress.attemptedAt),
                    })}
                  </p>
                )}
              </>
            ) : null}
            <p className={styles.cardMeta}>
              {t('certification.course.videoWatchedPercent', {
                percent: Math.max(0, Math.min(100, Math.round(videoWatchedPercent))),
              })}
            </p>
          </div>
        )}
        {!previewOnly && (
          <>
            <div className={styles.cardActions}>
              <button
                type="button"
                className={styles.cardAction}
                disabled={!interactive}
                onClick={() => interactive && onWatchVideo?.()}
              >
                <PlayIcon />
                {tx('certification.course.watchVideo')}
              </button>
              <div className={styles.cardQuizRow}>
                <button
                  type="button"
                  className={styles.cardAction}
                  disabled={!interactive}
                  onClick={() => interactive && onTakeQuiz?.()}
                >
                  <DocumentIcon />
                  {passed
                    ? tx('certification.course.retakeQuiz')
                    : tx('certification.course.takeQuiz')}
                </button>
                {userProgress && (
                  <span className={styles.cardScore}>
                    {t('certification.course.scoreSummary', {
                      correct: userProgress.correctCount,
                      total: userProgress.totalCount,
                      score: userProgress.score,
                    })}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className={passed ? styles.cardBtnOutlined : styles.cardBtnPrimary}
              disabled={!interactive}
              onClick={() => interactive && onStartLesson?.()}
            >
              {passed
                ? tx('certification.course.reviewLesson')
                : tx('certification.course.startLesson')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
